//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import SamplingModel from '../models/sampling.model';
import FarmSiteModel from '../models/farm-site.model';

/**
 * @service SamplingService
 * @description Service for managing biological sampling data within the aquaculture system.
 * Sampling involves measuring the physical attributes of the cultured species (shrimp/fish) to track growth and health.
 * Key metrics managed include:
 * - **ABW (Average Body Weight)**: The average weight of individual animals.
 * - **ADG (Average Daily Growth)**: The rate of weight gain per day.
 * - **Count**: The estimated population or density.
 *
 * This service provides functionality to:
 * 1. **Retrieve Raw Data**: Fetch sampling logs and activities.
 * 2. **Generate Summaries**: Aggregate data for dashboards (e.g., average ABW across a farm).
 * 3. **Spatial Analysis**: Group sampling data by location (Farm Site/Section).
 * 4. **Detailed Reporting**: Provide granular data down to the pond level.
 *
 * The service interacts with the `/blubay/sampling/` backend endpoint.
 *
 * @feature 1. Retrieve sampling activities and daily counts.
 * @feature 2. Calculate and fetch ABW, ADG, and Count summaries.
 * @feature 3. Provide location-based sampling analytics.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<SamplingModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/sampling/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class SamplingService extends Service<SamplingModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/sampling/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/sampling/');
  }

  /**
   * @method getAllSamplingActivities
   * @async
   * @description Fetch all sampling activities from the backend.
   *
   * Retrieves a large dataset (pageSize=20000) of sampling records, ordered by creation date ('new').
   *
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to an array of SamplingModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * samplingService.getAllSamplingActivities().then(activities => {
   *   console.log('Sampling Logs:', activities);
   * });
   */
  public getAllSamplingActivities() {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getABWSummary
   * @async
   * @description Fetch Average Body Weight (ABW) summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the ABW summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `abw/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getABWSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}abw/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCountSummary
   * @async
   * @description Fetch population Count summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the Count summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `count/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getCountSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}count/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getADGSummary
   * @async
   * @description Fetch Average Daily Growth (ADG) summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the ADG summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `adg/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getADGSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}adg/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getABWLocation
   * @async
   * @description Fetch ABW data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the ABW location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `abw/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getABWLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}abw/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCountLocation
   * @async
   * @description Fetch Count data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the Count location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `count/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getCountLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}count/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getADGLocation
   * @async
   * @description Fetch ADG data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the ADG location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `adg/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getADGLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}adg/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getABWDetails
   * @async
   * @description Fetch detailed ABW metrics broken down by pond and section.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the detailed ABW data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pondName/sectionName/abw/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getABWDetails(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}pondName/sectionName/abw/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getADGDetails
   * @async
   * @description Fetch detailed ADG metrics broken down by pond and section.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the detailed ADG data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pondName/sectionName/adg/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getADGDetails(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}pondName/sectionName/adg/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCountDetails
   * @async
   * @description Fetch detailed Count metrics broken down by pond and section.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to the detailed Count data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pondName/sectionName/count/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getCountDetails(farmsites: FarmSiteModel[]) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}pondName/sectionName/count/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSamplingsByPondIdCultureId
   * @async
   * @description Fetch the latest sampling records for a specific pond and culture cycle.
   *
   * Retrieves the most recent 5 records (offset 0, limit 5) to show trends.
   *
   * @param {string} pondId - The unique identifier of the pond.
   * @param {string} pondCultureId - The unique identifier of the culture cycle.
   * @returns {Promise<Array<SamplingModel>>} Promise resolving to an array of recent sampling records.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSamplingsByPondIdCultureId(pondId: string, pondCultureId: string) {
    return new Promise<Array<SamplingModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SamplingModel>>(`${this.baseUrl}${this.serviceUrl}latestPondSampling/pondId/${pondId}/pondCultureId/${pondCultureId}/offSet/0/limit/5`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
